/**
 * Global Error Handlers
 *
 * Installs global error handlers to automatically capture unhandled errors,
 * unhandled promise rejections, and network errors. Should be installed once
 * at app initialization.
 *
 * Handlers:
 * - window.onerror: Catches uncaught exceptions
 * - window.onunhandledrejection: Catches unhandled promise rejections
 * - fetch interceptor: Captures HTTP errors (4xx, 5xx)
 *
 * @module lib/datahub/errorLogging/globalHandlers
 *
 * @example
 * ```typescript
 * import { installGlobalHandlers, installNetworkInterceptor } from './globalHandlers';
 *
 * // In App.tsx or main.tsx
 * useEffect(() => {
 *   const cleanup1 = installGlobalHandlers();
 *   const cleanup2 = installNetworkInterceptor();
 *
 *   return () => {
 *     cleanup1();
 *     cleanup2();
 *   };
 * }, []);
 * ```
 */

import { captureError } from './captureError';
import { getMountedSurfaces } from './surfaceRegistry';
import { installPageVisibilityClock, getPageVisibilitySnapshot } from './pageVisibilityClock';

/**
 * True when the BROWSER redacted this error because it came from a script on
 * another origin.
 *
 * The signature is the browser's, not ours: for a cross-origin throw the spec
 * requires `window.onerror` to be called with the message "Script error.", an
 * empty filename, and zero line/column — and `event.error` is null because the
 * error object itself is withheld. Measured on the live store: 144 of 144
 * stored "Script error." events carry exactly `{filename: "", lineno: 0,
 * colno: 0}`.
 *
 * The predicate keys on the ABSENCE OF A FILENAME rather than on the message
 * text, because the message string varies by browser ("Script error." in
 * Chrome, "Script error" elsewhere) while the redaction is always total: a
 * same-origin error always arrives with a filename. `event.error == null` is
 * required as well, so a real error object with an odd filename is never
 * treated as masked.
 *
 * FAILURE DIRECTION: if this ever fails to match a genuinely masked error, that
 * error is reported the old way — with a fabricated stack, which is today's
 * behaviour and is visible. If it wrongly matched a real error we would LOSE a
 * real stack, which is silent. Requiring both conditions keeps it on the
 * visible side.
 */
function isMaskedCrossOriginError(event: ErrorEvent): boolean {
  return event.error == null && !event.filename;
}

/**
 * Remove a synthesised Error's stack in place and return it.
 *
 * `Error.prototype.stack` is `string | undefined`, and `undefined` is exactly
 * the right value: `captureError`'s `parseStackTrace` returns `{}` for it, the
 * payload carries no `file`/`line`/`column`/`functionName`, and the backend
 * fingerprint falls back to its documented `"unknown"` stack location. Nothing
 * downstream needs to learn a new shape — they all already handle a stackless
 * error, which is why this is a two-line fix rather than a schema change.
 */
function strippedStack(error: Error): Error {
  error.stack = undefined;
  return error;
}

/**
 * The distinct origins of `<script>` tags on the page that are not ours.
 *
 * On a masked error this is the entire suspect list, and it is the only part of
 * it the page can still tell us. Origins only — never the full src, which can
 * carry an API key in its query string (the Maps loader does exactly that).
 *
 * Never throws: a malformed `src` must not take down the error handler.
 */
function crossOriginScriptOrigins(): string[] {
  try {
    const origins = new Set<string>();
    const scripts = document.querySelectorAll('script[src]');
    scripts.forEach((el) => {
      const src = (el as HTMLScriptElement).src;
      if (!src) return;
      try {
        const { origin } = new URL(src, window.location.href);
        if (origin && origin !== window.location.origin) origins.add(origin);
      } catch {
        /* an unparseable src tells us nothing; skip it */
      }
    });
    return Array.from(origins).sort();
  } catch {
    return [];
  }
}

/**
 * Install global error handlers
 *
 * Installs listeners for uncaught errors and unhandled promise rejections.
 * Filters out benign errors like ResizeObserver. Returns a cleanup function
 * to remove handlers (important for hot module reload in development).
 *
 * @returns Cleanup function to remove event listeners
 *
 * @example
 * ```typescript
 * const cleanup = installGlobalHandlers();
 *
 * // Later, to cleanup:
 * cleanup();
 * ```
 */
export function installGlobalHandlers(): () => void {
  const cleanupFns: Array<() => void> = [];

  cleanupFns.push(installPageVisibilityClock());

  // Handle uncaught errors
  const errorHandler = (event: ErrorEvent) => {
    // Ignore ResizeObserver errors - they're usually benign
    if (event.message?.includes('ResizeObserver')) {
      return;
    }

    // Handle chunk load errors (stale cache after deployment)
    const isChunkError =
      event.error?.name === 'ChunkLoadError' ||
      event.message?.includes('Loading chunk') ||
      event.message?.includes('Unexpected token');

    if (isChunkError) {
      const lastReload = sessionStorage.getItem('chunk_error_reload');
      const now = Date.now();
      if (!lastReload || (now - parseInt(lastReload, 10)) > 60000) {
        sessionStorage.setItem('chunk_error_reload', now.toString());
        window.location.reload();
        return;
      }
    }

    const masked = isMaskedCrossOriginError(event);

    captureError({
      // 🔴 THE STACK IS DROPPED WHENEVER THE BROWSER GAVE US NO ERROR OBJECT.
      // `new Error(...)` built HERE has THIS file's frames, and `captureError`
      // parses the first frame into `file`/`line` — which is how eight months
      // of "Script error." groups came to name `main.<hash>.js:172`, a line
      // inside the reporter, as the crash site. There is no version of that
      // stack worth keeping: it describes the observer, not the event.
      //
      // Deliberately keyed on `event.error == null`, not on `masked`: a
      // synthesised Error's stack is equally fictional in the rarer
      // no-error-object-but-filename-present case, and fixing only the masked
      // shape would leave the same defect behind a narrower door.
      error: event.error || strippedStack(new Error(event.message)),
      capturePoint: 'window_onerror',
      additionalContext: {
        // The browser's own values, verbatim — including the empty filename and
        // the zero line/column that are the PROOF of redaction. Storing them
        // unchanged is what lets a reader tell "masked" from "we did not look".
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        ...(masked
          ? {
              masked: true,
              maskedReason: 'cross_origin_script',
              // The entire suspect list the page can still name.
              crossOriginScripts: crossOriginScriptOrigins(),
              // ...and what of OURS was on screen at the time.
              mountedSurfaces: getMountedSurfaces(),
              // ...and whether the page was on screen (Navigate sends the tab to the background).
              ...getPageVisibilitySnapshot(),
            }
          : {}),
      },
    });
  };

  window.addEventListener('error', errorHandler);
  cleanupFns.push(() => window.removeEventListener('error', errorHandler));

  // Handle unhandled promise rejections
  const rejectionHandler = (event: PromiseRejectionEvent) => {
    const error = event.reason instanceof Error
      ? event.reason
      : new Error(String(event.reason) || 'Unhandled Promise Rejection');

    // Enhanced logging for Firestore permission errors — log the path for debugging
    if (error.message?.includes('Missing or insufficient permissions')) {
      const stack = error.stack || '';
      // Try to extract collection path from the error details
      const pathMatch = stack.match(/documents\/([^\s"']+)/);
      console.warn(
        '[Firestore Permission Error]',
        pathMatch ? `Path: ${pathMatch[1]}` : 'Path unknown',
        'Full error:', error,
      );
    }

    captureError({
      error,
      capturePoint: 'unhandled_rejection',
    });
  };

  window.addEventListener('unhandledrejection', rejectionHandler);
  cleanupFns.push(() => window.removeEventListener('unhandledrejection', rejectionHandler));

  return () => {
    cleanupFns.forEach(fn => fn());
  };
}

/**
 * Install network interceptor for fetch
 *
 * Network error reporting is now handled via contextCapture callbacks
 * (handleNetworkError in consoleAndNetworkCapture.ts). This function
 * is kept as a no-op for backwards compatibility with initializeErrorLogging().
 *
 * @returns No-op cleanup function
 */
export function installNetworkInterceptor(): () => void {
  // Network error reporting is now handled via contextCapture callbacks
  // (handleNetworkError in consoleAndNetworkCapture.ts)
  return () => {};
}
